import React, {useCallback, useMemo, useState} from 'react';
import {ResizableBox, type ResizeCallbackData} from 'react-resizable';

import {TOP_NAV_HEIGHT} from '../../util/constants';
import {
  DEFAULT_WINDOW_HEIGHT,
  DEFAULT_WINDOW_WIDTH,
  MAX_WINDOW_HEIGHT,
  MAX_WINDOW_WIDTH,
  MIN_WINDOW_HEIGHT,
  MIN_WINDOW_WIDTH,
} from './constants';
import {useViewportHeight} from './useViewportHeight';

const VERTICAL_PADDING = 32; // Buffer for chat window from the bottom of the viewport
const minConstraints: [number, number] = [MIN_WINDOW_WIDTH, MIN_WINDOW_HEIGHT];
const handleSize: [number, number] = [20, 20];

export const WBAgentWindow: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [desiredWidth, setDesiredWidth] = useState(DEFAULT_WINDOW_WIDTH);
  const [desiredHeight, setDesiredHeight] = useState(DEFAULT_WINDOW_HEIGHT);
  const viewportHeight = useViewportHeight();

  const availableHeight = viewportHeight - TOP_NAV_HEIGHT - VERTICAL_PADDING;
  const effectiveHeight = Math.max(
    MIN_WINDOW_HEIGHT,
    Math.min(desiredHeight, availableHeight)
  );

  const maxConstraints: [number, number] = useMemo(
    () => [MAX_WINDOW_WIDTH, Math.min(MAX_WINDOW_HEIGHT, availableHeight)],
    [availableHeight]
  );

  const handleResize = useCallback(
    (_e: React.SyntheticEvent, {size: newSize}: ResizeCallbackData) => {
      setDesiredWidth(newSize.width);
      setDesiredHeight(newSize.height);
    },
    []
  );

  return (
    <ResizableBox
      width={desiredWidth}
      height={effectiveHeight}
      handle={
        <span className="absolute bg-transparent z-[1] hover:bg-teal-450 -bottom-4 -right-4 w-10 h-10 cursor-se-resize" />
      }
      handleSize={handleSize}
      minConstraints={minConstraints}
      maxConstraints={maxConstraints}
      onResize={handleResize}>
      <div
        className="flex flex-col"
        style={{width: desiredWidth, height: effectiveHeight}}>
        {children}
      </div>
    </ResizableBox>
  );
};
